/** Date / time / datetime widgets, always the record's LOCAL wall clock (the tour's
 *  zone, which the FieldSlot's label row names) — never browser-local.
 *
 *  MUI pickers with the date-fns adapter have no zone support, so they are fed plain
 *  `Date`s whose local fields carry the wall clock (the P1-a `zonedTime` helper idiom):
 *  wire `YYYY-MM-DD` / `HH:MM` / `YYYY-MM-DDTHH:MM` → `WallClock` → picker Date and
 *  back, with no instant conversion in between, so a wall time round-trips exactly. A
 *  half-typed value is reported through `onInvalid` and never reaches the draft. Each
 *  picker sits in its own `LocalizationProvider`. ISO-like display formats: an ops team
 *  across locales must not misread 03/10 as 10 March.
 */
import { DatePicker, DateTimePicker, LocalizationProvider, TimePicker } from '@mui/x-date-pickers-pro';
import { AdapterDateFns } from '@mui/x-date-pickers-pro/AdapterDateFnsV3';

import {
  formatWallClock,
  parseWallClock,
  pickerDateToWallClock,
  wallClockToPickerDate,
} from '@/utils/zonedTime';

import type { RecordWidgetProps } from '../../editor/types';

export const DATE_FORMAT = 'yyyy-MM-dd';
export const TIME_FORMAT = 'HH:mm';
export const DATETIME_FORMAT = 'yyyy-MM-dd HH:mm';

const TIME_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

/** Wire value → picker Date (local fields = the wall clock); null when empty/invalid. */
export function wireToPicker(widget: 'date' | 'time' | 'datetime', value: unknown): Date | null {
  if (typeof value !== 'string' || value === '') return null;
  if (widget === 'time') {
    const m = TIME_RE.exec(value);
    return m ? new Date(2000, 0, 1, Number(m[1]), Number(m[2])) : null;
  }
  const w = parseWallClock(widget === 'date' ? value.slice(0, 10) : value.slice(0, 16));
  return w ? wallClockToPickerDate(w) : null;
}

/** Picker Date → wire value. */
export function pickerToWire(widget: 'date' | 'time' | 'datetime', d: Date): string {
  const w = pickerDateToWallClock(d);
  if (widget === 'date') return formatWallClock(w, { time: false });
  if (widget === 'time') return `${String(w.hour).padStart(2, '0')}:${String(w.minute).padStart(2, '0')}`;
  return formatWallClock(w);
}

const INVALID: Record<'date' | 'time' | 'datetime', string> = {
  date: 'Enter a complete date (YYYY-MM-DD)',
  time: 'Enter a complete time (HH:MM, 24-hour)',
  datetime: 'Enter a complete date and time (YYYY-MM-DD HH:MM)',
};

function pickerOnChange(widget: 'date' | 'time' | 'datetime', p: RecordWidgetProps) {
  return (d: Date | null) => {
    if (d === null) {
      p.onInvalid(null);
      p.onChange(null);
    } else if (Number.isNaN(d.getTime())) {
      p.onInvalid(INVALID[widget]);
    } else {
      p.onInvalid(null);
      p.onChange(pickerToWire(widget, d));
    }
  };
}

function textFieldProps(p: RecordWidgetProps, width: number) {
  return {
    id: p.id,
    size: 'small' as const,
    fullWidth: true,
    error: p.error,
    // Typing a date section by section is one undo step per focus session.
    onFocus: p.onFocus,
    onBlur: p.onBlur,
    inputProps: { 'aria-describedby': p.describedBy },
    sx: { maxWidth: width },
  };
}

export function DateWidget(p: RecordWidgetProps) {
  const onChange = pickerOnChange('date', p);
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DatePicker
        value={wireToPicker('date', p.value)}
        onChange={onChange}
        format={DATE_FORMAT}
        disabled={p.disabled}
        reduceAnimations
        slotProps={{ textField: textFieldProps(p, 220) }}
      />
    </LocalizationProvider>
  );
}

export function TimeWidget(p: RecordWidgetProps) {
  const onChange = pickerOnChange('time', p);
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <TimePicker
        value={wireToPicker('time', p.value)}
        onChange={onChange}
        format={TIME_FORMAT}
        ampm={false}
        disabled={p.disabled}
        reduceAnimations
        slotProps={{ textField: textFieldProps(p, 160) }}
      />
    </LocalizationProvider>
  );
}

export function DateTimeWidget(p: RecordWidgetProps) {
  const onChange = pickerOnChange('datetime', p);
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <DateTimePicker
        value={wireToPicker('datetime', p.value)}
        onChange={onChange}
        format={DATETIME_FORMAT}
        ampm={false}
        disabled={p.disabled}
        reduceAnimations
        slotProps={{ textField: textFieldProps(p, 260) }}
      />
    </LocalizationProvider>
  );
}
